import React, { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Card, Divider, Typography } from '@mui/material';
import { ErrorAlert } from '@warehouse/shared/ui';
import {
	SERVICE_LOG_AUTO_REFRESH_MS,
	serviceLogStoreConfig,
	serviceLogStoreSelector,
	ServiceLogStoreProvider,
	useServiceLogStore,
} from '@warehouse/service-log/domain';
import { ServiceLogGrid } from './ui/ServiceLogGrid';
import { ServiceLogSearchBar } from './ui/ServiceLogSearchBar';
import { ServiceLogToolbar } from './ui/ServiceLogToolbar';

interface ServiceLogPageProps {
	/**
	 * @deprecated The method should only be used for component tests
	 */
	disableVirtualization?: boolean;
}

/**
 * Service Log: every Broker job of the tenant, with a Run Service flow (WH-4932).
 * The header mirrors the Object Browser's PageHeader (same Typography variant, row height and spacing).
 */
export function ServiceLogPage({ disableVirtualization }: ServiceLogPageProps) {
	const config = useMemo(() => serviceLogStoreConfig(), []);

	return (
		<ServiceLogStoreProvider config={config}>
			<ServiceLogStoreEffects />
			<Wrapper data-testid="service-log-page">
				<CardWrapper>
					<Card style={{ boxShadow: 'none', borderRadius: 8 }} sx={{ minWidth: 275, overflow: 'visible' }}>
						<TopActionWrapper>
							<Typography variant="h4Bold">Service Log</Typography>
						</TopActionWrapper>
						<Divider />
						<ServiceLogSearchBar />
					</Card>
					<Card
						style={{
							flex: 1,
							minHeight: 0,
							display: 'flex',
							flexDirection: 'column',
							boxShadow: 'none',
							borderRadius: 8,
						}}
					>
						<ContentWrapper>
							<ServiceLogToolbar />
							<ServiceLogFetchError />
							<ServiceLogGrid disableVirtualization={disableVirtualization} />
						</ContentWrapper>
					</Card>
				</CardWrapper>
			</Wrapper>
		</ServiceLogStoreProvider>
	);
}

/** Reloads when the page or the filters sent to the Broker change, and keeps polling while listed jobs are still open. */
function ServiceLogStoreEffects() {
	const page = useServiceLogStore(serviceLogStoreSelector.page);
	const perPage = useServiceLogStore(serviceLogStoreSelector.perPage);
	const searchFiltersKey = useServiceLogStore(serviceLogStoreSelector.searchFiltersKey);
	const hasOpenJobs = useServiceLogStore(serviceLogStoreSelector.hasOpenJobs);
	const { load, refresh } = useServiceLogStore(serviceLogStoreSelector.actions);

	useEffect(() => {
		load();
	}, [load, page, perPage, searchFiltersKey]);

	useEffect(() => {
		if (!hasOpenJobs) return undefined;
		const interval = setInterval(() => refresh(), SERVICE_LOG_AUTO_REFRESH_MS);
		return () => clearInterval(interval);
	}, [hasOpenJobs, refresh]);

	return null;
}

function ServiceLogFetchError() {
	const isFetchError = useServiceLogStore(serviceLogStoreSelector.isFetchError);
	if (!isFetchError) return null;
	return <ErrorAlert errorMessage="Failed to load the jobs" />;
}

// The app content area is a fixed-height flex row (App.tsx); the page fills it so the grid scrolls inside its card.
const Wrapper = styled.div(
	({ theme }) => css`
		background-color: ${theme.palette.light.backgroundAlt};
		box-sizing: border-box;
		display: flex;
		height: 100%;
		justify-content: center;
		width: 100%;
	`,
);

const CardWrapper = styled.div(
	({ theme }) => css`
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		gap: ${theme.spacing(2)};
		height: 100%;
		padding: ${theme.spacing(2)};
		width: 100%;
	`,
);

const TopActionWrapper = styled.div(
	({ theme }) => css`
		align-items: center;
		display: flex;
		height: 26px;
		justify-content: space-between;
		padding: ${theme.spacing(2)};
	`,
);

const ContentWrapper = styled.div(
	({ theme }) => css`
		box-sizing: border-box;
		display: flex;
		flex: 1;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		padding: ${theme.spacing(2)};
	`,
);
